import {Wrapper, Button} from "../../components";
import {useQuery} from "@tanstack/react-query";
import {getMarket, getTickers, getCurrencies} from "../../utils/GetData";
import { useState, useMemo } from "react";

function MarketList() {
    const [activeTab, setActiveTab] = useState('all');

    const {data: marketData, isLoading: marketDataLoading, error: marketDataError} = useQuery({
        queryKey: 'markets',
        queryFn: getMarket,
    });

    const {data: tickersData, isLoading: tickersDataLoading, error: tickersDataError} = useQuery({
        queryKey: 'tickers',
        queryFn: getTickers,
    });

    const {data: currenciesData, isLoading: currenciesDataLoading, error: currenciesDataError} = useQuery({
        queryKey: 'currencies',
        queryFn: getCurrencies,
    });

    // Veri hazır olduğunda işlenecek birleştirilmiş veri
    const combinedData = useMemo(() => {
        if (!marketData || !tickersData || !currenciesData) return [];
        
        return marketData.filter(item => item.state !== 'hidden').map((item) => {
            return {
                ...item,
                ticker: tickersData[item.id],
                priceChangePercent: parseFloat(tickersData[item.id]?.ticker?.price_change_percent || 0)
            }
        });
    }, [marketData, tickersData, currenciesData]);
    
    // Filtreleme fonksiyonu
    const filteredData = useMemo(() => {
        if (activeTab === 'all') {
            return combinedData;
        } else if (activeTab === 'gainers') {
            return [...combinedData].filter(item => item.priceChangePercent > 0)
                .sort((a, b) => b.priceChangePercent - a.priceChangePercent);
        } else if (activeTab === 'losers') {
            return [...combinedData].filter(item => item.priceChangePercent <= 0)
                .sort((a, b) => a.priceChangePercent - b.priceChangePercent);
        }
        return combinedData;
    }, [combinedData, activeTab]);

    if (marketDataLoading || tickersDataLoading || currenciesDataLoading) {
        return (
            <Wrapper>
                <div className="mt-24 flex justify-center">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-primary animate-pulse"></div>
                        <div className="w-3 h-3 rounded-full bg-primary animate-pulse delay-150"></div>
                        <div className="w-3 h-3 rounded-full bg-primary animate-pulse delay-300"></div>
                        <span className="text-primary ml-2">Yükleniyor...</span>
                    </div>
                </div>
            </Wrapper>
        )
    }

    if (marketDataError || tickersDataError || currenciesDataError) {
        return (
            <Wrapper>
                <div className="mt-24 flex justify-center">
                    <div className="text-danger flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>Veri yüklenirken bir hata oluştu.</span>
                    </div>
                </div>
            </Wrapper>
        )
    }

    const renderMarketList = (market, id) => {
        const foundCurrency = currenciesData.find((item) => item.id === market.base_unit);
        const icon = foundCurrency ? foundCurrency.icon_url || "https://gmskripto.com/coin-icons/gms.png" : "https://gmskripto.com/coin-icons/gms.png";
        const last = parseFloat(market.ticker.ticker.last);
        const quoteUnit = market.quote_unit.toUpperCase();
        const volume = parseFloat(market.ticker.ticker.volume).toFixed(2);
        const priceChangePercent = parseFloat(market.ticker.ticker.price_change_percent);
        const isPositive = priceChangePercent > 0;
        
        return (
            <tr key={market.id} className="border-b border-dark-line/30 hover:bg-dark-backgroundOnDark/50 transition-colors">
                <td className="py-4 px-4">{id + 1}</td>
                <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-dark-backgroundOnDark/50 flex items-center justify-center">
                            <img src={icon} alt={market.name} className="w-6 h-6"/>
                        </div>
                        <div>
                            <div className="font-medium text-white">{market?.name.toUpperCase()}</div>
                            <div className="text-secondaryTwo text-xs">{market?.base_unit.toUpperCase()}</div>
                        </div>
                    </div>
                </td>
                <td className="py-4 px-4 font-medium text-white">{quoteUnit} {last}</td>
                <td className={`py-4 px-4 ${isPositive ? 'text-success' : 'text-danger'}`}>
                    <div className="flex items-center gap-1">
                        {isPositive ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M12 13a1 1 0 100 2h5a1 1 0 001-1v-5a1 1 0 10-2 0v2.586l-4.293-4.293a1 1 0 00-1.414 0L8 9.586l-4.293-4.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0L11 9.414 14.586 13H12z" clipRule="evenodd" />
                            </svg>
                        )}
                        <span className="font-medium">{market.ticker.ticker.price_change_percent}</span>
                    </div>
                </td>
                <td className="py-4 px-4 text-secondaryTwo">{quoteUnit} {volume}</td>
                <td className="py-4 px-4">
                    <Button 
                        onClick={() => window.location.href = `https://exchange.gmskripto.com/protrade/${market.id}`}
                        className="py-2 px-6 text-sm bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary border-none"
                    >
                        İşlem Yap
                    </Button>
                </td>
            </tr>
        )
    }

    return (
        <Wrapper>
            <div className="mt-24 bg-dark-backgroundOnDark/30 rounded-2xl p-6 border border-dark-line/30">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h2 className="text-surface-main font-bold text-3xl md:text-4xl">Piyasalar</h2>
                        <p className="text-secondaryTwo mt-2">En popüler kripto para çiftlerini keşfedin</p>
                    </div>
                    
                    <div className="flex space-x-2 bg-dark-background/50 p-1 rounded-lg">
                        <button 
                            onClick={() => setActiveTab('all')}
                            className={`px-4 py-2 text-sm rounded-md transition-colors ${activeTab === 'all' ? 'bg-primary text-white' : 'text-secondaryTwo hover:text-white'}`}
                        >
                            Tümü
                        </button>
                        <button 
                            onClick={() => setActiveTab('gainers')}
                            className={`px-4 py-2 text-sm rounded-md transition-colors ${activeTab === 'gainers' ? 'bg-primary text-white' : 'text-secondaryTwo hover:text-white'}`}
                        >
                            Yükselenler
                        </button>
                        <button 
                            onClick={() => setActiveTab('losers')}
                            className={`px-4 py-2 text-sm rounded-md transition-colors ${activeTab === 'losers' ? 'bg-primary text-white' : 'text-secondaryTwo hover:text-white'}`}
                        >
                            Düşenler
                        </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto rounded-xl border border-dark-line/20">
                    <table className="min-w-full text-white">
                        <thead className="bg-dark-background/50">
                            <tr>
                                <th className="py-3 px-4 text-left text-xs font-medium text-secondaryTwo uppercase tracking-wider">#</th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-secondaryTwo uppercase tracking-wider">İsim</th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-secondaryTwo uppercase tracking-wider">Fiyat</th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-secondaryTwo uppercase tracking-wider">Değişim (24s)</th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-secondaryTwo uppercase tracking-wider">Hacim (24s)</th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-secondaryTwo uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-line/10 bg-dark-background/20">
                            {filteredData?.slice(0, 10)?.map((crypto, id) => (
                                renderMarketList(crypto, id)
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {filteredData.length === 0 && (
                    <div className="py-8 text-center text-secondaryTwo">
                        Bu kategoride gösterilecek kripto para bulunamadı.
                    </div>
                )}
                
                <div className="mt-6 text-center">
                    <a 
                        href="https://exchange.gmskripto.com/markets" 
                        className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
                    >
                        <span>Tüm Piyasaları Görüntüle</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </a>
                </div>
            </div>
        </Wrapper>
    )
}

export {MarketList}
